import React, { useState, useEffect } from "react";
import Layout from "../components/ui/Layout";
import { motion } from "framer-motion";
import { ArrowRight, Check, Copy } from "lucide-react";

// ─── Animation helpers ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

// ─── Color token data ─────────────────────────────────────────────────────────
const colorTokens = [
  {
    group: "Primary",
    swatches: [
      { name: "Primary", token: "bg-primary", hex: "#212121", textLight: true },
      { name: "Primary / 80", token: "bg-primary/80", hex: "#212121 / 80%", textLight: true },
      { name: "Primary / 40", token: "bg-primary/40", hex: "#212121 / 40%", textLight: true },
    ],
  },
  {
    group: "Neutrals",
    swatches: [
      { name: "Black", token: "bg-black", hex: "#000000", textLight: true },
      { name: "Gray 900", token: "bg-gray-900", hex: "#111827", textLight: true },
      { name: "Gray 700", token: "bg-gray-700", hex: "#374151", textLight: true },
      { name: "Gray 500", token: "bg-gray-500", hex: "#6B7280", textLight: true },
      { name: "Gray 300", token: "bg-gray-300", hex: "#D1D5DB", textLight: false },
      { name: "Gray 100", token: "bg-gray-100", hex: "#F3F4F6", textLight: false },
      { name: "White", token: "bg-white", hex: "#FFFFFF", textLight: false, border: true },
    ],
  },
  {
    group: "Semantic",
    swatches: [
      { name: "Destructive", token: "bg-red-500", hex: "#EF4444", textLight: true },
      { name: "Success", token: "bg-green-600", hex: "#16A34A", textLight: true },
      { name: "Warning", token: "bg-amber-400", hex: "#FBBF24", textLight: false },
      { name: "Info", token: "bg-blue-500", hex: "#3B82F6", textLight: true },
    ],
  },
];

// ─── Type scale ───────────────────────────────────────────────────────────────
const typeScale = [
  { name: "Display", size: "text-5xl", weight: "font-light", sample: "Design That Speaks", meta: "5xl / font-light" },
  { name: "Heading 1", size: "text-3xl", weight: "font-light", sample: "Research & Discovery", meta: "3xl / font-light" },
  { name: "Heading 2", size: "text-2xl", weight: "font-light", sample: "User Journey Mapping", meta: "2xl / font-light" },
  { name: "Subheading", size: "text-xl", weight: "font-medium", sample: "Key Insights", meta: "xl / font-medium" },
  { name: "Body Large", size: "text-lg", weight: "font-normal", sample: "Product design shaped by research, accessibility, and real human behaviour.", meta: "lg / font-normal" },
  { name: "Body", size: "text-base", weight: "font-normal", sample: "We craft accessible, thoughtful experiences that put people first in every interaction.", meta: "base / font-normal" },
  { name: "Body Small", size: "text-sm", weight: "font-normal", sample: "Outside of work: Rednote content creator, mom of a kitten.", meta: "sm / font-normal" },
  { name: "Label / Tag", size: "text-xs", weight: "font-semibold", sample: "UX RESEARCH  ·  INTERACTION DESIGN  ·  ACCESSIBILITY", meta: "xs / font-semibold / uppercase tracking-wider" },
];

// ─── Spacing scale ────────────────────────────────────────────────────────────
const spacingScale = [
  { token: "space-1", px: "4px", label: "1" },
  { token: "space-2", px: "8px", label: "2" },
  { token: "space-4", px: "16px", label: "4" },
  { token: "space-6", px: "24px", label: "6" },
  { token: "space-8", px: "32px", label: "8" },
  { token: "space-12", px: "48px", label: "12" },
  { token: "space-16", px: "64px", label: "16" },
  { token: "space-20", px: "80px", label: "20" },
  { token: "space-24", px: "96px", label: "24" },
];

// ─── Radius scale ─────────────────────────────────────────────────────────────
const radiusScale = [
  { name: "None", class: "rounded-none", value: "0px" },
  { name: "sm", class: "rounded-sm", value: "2px" },
  { name: "md", class: "rounded-md", value: "4px" },
  { name: "lg", class: "rounded-lg", value: "8px" },
  { name: "xl", class: "rounded-xl", value: "12px" },
  { name: "2xl", class: "rounded-2xl", value: "16px" },
  { name: "Full", class: "rounded-full", value: "9999px" },
];

// ─── Copy-to-clipboard helper ─────────────────────────────────────────────────
function CopyChip({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors group"
    >
      {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
      <span className="font-mono">{value}</span>
    </button>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ id, children }) {
  return (
    <section
      id={id}
      className="py-16 border-b border-gray-800 scroll-mt-24"
    >
      {children}
    </section>
  );
}

// ─── Section label (the design pattern itself) ────────────────────────────────
function SectionLabel({ children }) {
  return (
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">
      {children}
    </p>
  );
}

// ─── Side nav ─────────────────────────────────────────────────────────────────
const navItems = [
  { label: "Introduction", id: "intro" },
  { label: "Colors", id: "colors" },
  { label: "Typography", id: "typography" },
  { label: "Spacing", id: "spacing" },
  { label: "Border Radius", id: "radius" },
  { label: "Buttons", id: "buttons" },
  { label: "Tags & Pills", id: "tags" },
  { label: "Cards", id: "cards" },
  { label: "Form Elements", id: "forms" },
  { label: "Patterns", id: "patterns" },
];

// ─── Main page ────────────────────────────────────────────────────────────────
const DesignSystemGuide = () => {
  const [activeSection, setActiveSection] = useState("intro");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    navItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* ── Page header ──────────────────────────────────────────────── */}
        <div className="border-b border-gray-800 py-16 px-6 sm:px-8 md:px-16">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Design System
              </motion.p>
              <motion.h1 variants={fadeUp} className="text-5xl font-light text-white mb-6">
                Young.Design
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-gray-400 max-w-xl">
                A living reference for the tokens, components, and patterns that make up this portfolio. Bold. Minimal. Human.
              </motion.p>
            </motion.div>
          </div>
        </div>

        {/* ── Body: sidebar + content ───────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-16 flex gap-16">
          {/* Sticky side nav */}
          <aside className="hidden lg:block w-48 shrink-0 py-16">
            <div className="sticky top-24 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`block text-sm py-1.5 transition-colors ${
                    activeSection === item.id
                      ? "text-white font-medium"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* ── Introduction ─────────────────────────────────────────── */}
            <Section id="intro">
              <SectionLabel>Introduction</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-6">Why a design system?</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { title: "Consistency", body: "One visual language across every page: same tokens, same decisions, no surprises." },
                  { title: "Efficiency", body: "Build once. Reuse everywhere. Spend more time solving problems, less time re-making buttons." },
                  { title: "Scalability", body: "When the brand evolves, change the token. Everything updates." },
                ].map((card) => (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="border border-gray-800 rounded-lg p-6"
                  >
                    <h3 className="text-base font-medium text-white mb-2">{card.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{card.body}</p>
                  </motion.div>
                ))}
              </div>
            </Section>

            {/* ── Colors ───────────────────────────────────────────────── */}
            <Section id="colors">
              <SectionLabel>Foundation</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Colors</h2>
              <div className="space-y-10">
                {colorTokens.map((group) => (
                  <div key={group.group}>
                    <p className="text-sm text-gray-500 mb-4">{group.group}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {group.swatches.map((s) => (
                        <motion.div
                          key={s.name}
                          initial={{ opacity: 0, scale: 0.96 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3 }}
                        >
                          <div
                            className={`${s.token} h-20 rounded-lg mb-2 ${s.border ? "border border-gray-200" : ""}`}
                          />
                          <p className={`text-sm font-medium mb-0.5 ${s.textLight ? "text-white" : "text-gray-200"}`}>
                            {s.name}
                          </p>
                          <CopyChip value={s.hex} />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Typography ────────────────────────────────────────────── */}
            <Section id="typography">
              <SectionLabel>Foundation</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Typography</h2>
              <p className="text-sm text-gray-500 mb-8">Typeface: <span className="font-mono text-gray-300">Helvetica Neue, sans-serif</span></p>
              <div className="space-y-0">
                {typeScale.map((item, i) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-8 py-6 border-b border-gray-800 last:border-0"
                  >
                    <div className="w-32 shrink-0">
                      <p className="text-xs text-gray-500">{item.name}</p>
                      <p className="text-xs font-mono text-gray-600 mt-0.5">{item.meta}</p>
                    </div>
                    <p className={`${item.size} ${item.weight} text-white leading-tight flex-1 min-w-0 truncate`}>
                      {item.sample}
                    </p>
                  </motion.div>
                ))}
              </div>
            </Section>

            {/* ── Spacing ───────────────────────────────────────────────── */}
            <Section id="spacing">
              <SectionLabel>Foundation</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Spacing</h2>
              <p className="text-sm text-gray-400 mb-8">
                All spacing follows Tailwind's 4px base unit. Container padding is standardized at{" "}
                <span className="font-mono text-gray-300">px-6 sm:px-8 md:px-12 lg:px-16</span>.
              </p>
              <div className="space-y-3">
                {spacingScale.map((item) => (
                  <div key={item.token} className="flex items-center gap-4">
                    <div className="w-20 shrink-0 text-right">
                      <span className="text-xs font-mono text-gray-500">{item.px}</span>
                    </div>
                    <div
                      className="bg-white rounded-sm h-4"
                      style={{ width: item.px }}
                    />
                    <span className="text-xs font-mono text-gray-600">{item.token}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Border Radius ─────────────────────────────────────────── */}
            <Section id="radius">
              <SectionLabel>Foundation</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Border Radius</h2>
              <div className="flex flex-wrap gap-6">
                {radiusScale.map((item) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex flex-col items-center gap-3"
                  >
                    <div
                      className={`w-16 h-16 bg-gray-700 border border-gray-600 ${item.class}`}
                    />
                    <div className="text-center">
                      <p className="text-xs text-gray-300">{item.name}</p>
                      <p className="text-xs font-mono text-gray-600">{item.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Section>

            {/* ── Buttons ───────────────────────────────────────────────── */}
            <Section id="buttons">
              <SectionLabel>Components</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Buttons</h2>
              <div className="space-y-10">
                {/* Variants */}
                <div>
                  <p className="text-sm text-gray-500 mb-5">Variants</p>
                  <div className="flex flex-wrap gap-4 items-center">
                    <button className="bg-white text-black px-6 py-3 text-sm font-medium rounded hover:bg-gray-100 transition-colors">
                      Primary
                    </button>
                    <button className="bg-transparent text-white border border-white px-6 py-3 text-sm font-medium rounded hover:bg-white hover:text-black transition-colors">
                      Secondary
                    </button>
                    <button className="bg-transparent text-gray-400 border border-gray-700 px-6 py-3 text-sm font-medium rounded hover:border-white hover:text-white transition-colors">
                      Ghost
                    </button>
                    <button className="text-white text-sm font-medium underline underline-offset-4 hover:text-gray-300 transition-colors">
                      Link
                    </button>
                    <button className="bg-gray-800 text-gray-500 px-6 py-3 text-sm font-medium rounded cursor-not-allowed" disabled>
                      Disabled
                    </button>
                  </div>
                </div>

                {/* Sizes */}
                <div>
                  <p className="text-sm text-gray-500 mb-5">Sizes</p>
                  <div className="flex flex-wrap gap-4 items-center">
                    <button className="bg-white text-black px-3 py-1.5 text-xs font-medium rounded">
                      Small
                    </button>
                    <button className="bg-white text-black px-6 py-3 text-sm font-medium rounded">
                      Medium
                    </button>
                    <button className="bg-white text-black px-8 py-4 text-base font-medium rounded">
                      Large
                    </button>
                  </div>
                </div>

                {/* With icons */}
                <div>
                  <p className="text-sm text-gray-500 mb-5">With Icon</p>
                  <div className="flex flex-wrap gap-4 items-center">
                    <button className="group bg-white text-black px-6 py-3 text-sm font-medium rounded flex items-center gap-2 hover:gap-3 transition-all">
                      View Case Study
                      <ArrowRight size={16} />
                    </button>
                    <button className="bg-transparent text-white border border-white px-6 py-3 text-sm font-medium rounded flex items-center gap-2 hover:bg-white hover:text-black transition-colors">
                      Learn More
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </Section>

            {/* ── Tags & Pills ──────────────────────────────────────────── */}
            <Section id="tags">
              <SectionLabel>Components</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Tags & Pills</h2>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500 mb-4">Default (used on project cards)</p>
                  <div className="flex flex-wrap gap-2">
                    {["UX Research", "Interaction Design", "Accessibility", "Prototyping", "User Testing", "Design System"].map(
                      (tag) => (
                        <span
                          key={tag}
                          className="border border-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      )
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-4">Solid</p>
                  <div className="flex flex-wrap gap-2">
                    {["Work", "About", "Life Notes"].map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-800 text-gray-300 text-xs px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-4">Inverted</p>
                  <div className="flex flex-wrap gap-2">
                    {["Featured", "New", "Case Study"].map((tag) => (
                      <span
                        key={tag}
                        className="bg-white text-black text-xs px-3 py-1 rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Section>

            {/* ── Cards ─────────────────────────────────────────────────── */}
            <Section id="cards">
              <SectionLabel>Components</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Cards</h2>

              {/* Portfolio card — matches homepage */}
              <div className="mb-10">
                <p className="text-sm text-gray-500 mb-6">Portfolio Card (Homepage)</p>
                <div className="bg-white rounded-lg p-6 grid sm:grid-cols-2 gap-8">
                  {[
                    {
                      img: "https://res.cloudinary.com/dlzvz9qj8/image/upload/v1761296912/Group_1000003471_1_mzuems.jpg",
                      alt: "IC 3.0 Case Study",
                      title: "Making complex access control look simple for 500+ security-heavy orgs",
                      tags: ["B2B", "System thinking", "Responsive design"],
                    },
                    {
                      img: "https://res.cloudinary.com/dlzvz9qj8/video/upload/v1792976602/ic-design-system-stack_xmdjgn.mp4",
                      alt: "IC 3.0 Design System",
                      title: "IC 3.0 design system: a scalable, sustainable, themeable UI framework",
                      tags: ["Design System", "Design QA"],
                    },
                  ].map((card) => (
                    <div key={card.title} className="overflow-hidden flex flex-col items-start space-y-3">
                      <img
                        src={card.img}
                        alt={card.alt}
                        className="w-full aspect-video object-cover hover:opacity-90 transition-opacity rounded-lg"
                      />
                      <div className="w-full text-left space-y-2">
                        <p className="text-sm text-gray-900 leading-relaxed hover:underline cursor-pointer">
                          {card.title}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {card.tags.map((tag) => (
                            <span
                              key={tag}
                              className="border border-gray-700 text-gray-900 text-xs px-2 py-1"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs font-mono text-gray-600 mt-3">
                  overflow-hidden · aspect-video · rounded-lg · border border-gray-700 text-xs px-2 py-1 (tags, no border-radius)
                </p>
              </div>

              <p className="text-sm text-gray-500 mb-6">Other Variants</p>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Default card */}
                <div className="border border-gray-800 rounded-lg overflow-hidden">
                  <div className="bg-gray-900 aspect-video" />
                  <div className="p-5">
                    <p className="text-xs text-gray-500 mb-2">UX Research · 2025</p>
                    <h3 className="text-base font-medium text-white mb-2">Project Title</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      A short description of the project and the outcome delivered.
                    </p>
                  </div>
                </div>

                {/* Hover lift card */}
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="border border-gray-700 rounded-lg overflow-hidden cursor-pointer hover:border-gray-500 transition-colors"
                >
                  <div className="bg-gray-800 aspect-video" />
                  <div className="p-5">
                    <p className="text-xs text-gray-500 mb-2">Interaction Design · 2024</p>
                    <h3 className="text-base font-medium text-white mb-2">Hover Lift Card</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Elevates slightly on hover to signal interactivity.
                    </p>
                  </div>
                </motion.div>

                {/* Minimal text card */}
                <div className="border border-gray-800 rounded-lg p-6 flex flex-col justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Stat</p>
                    <p className="text-4xl font-light text-white mb-2">94%</p>
                    <p className="text-sm text-gray-400">Task completion rate in usability testing</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-6">Q3 2025</p>
                </div>
              </div>
            </Section>

            {/* ── Form Elements ─────────────────────────────────────────── */}
            <Section id="forms">
              <SectionLabel>Components</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Form Elements</h2>
              <div className="max-w-sm space-y-5">
                {/* Text input */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Text Input
                  </label>
                  <input
                    type="text"
                    placeholder="Your name"
                    className="w-full bg-transparent border border-gray-700 text-white placeholder-gray-600 rounded px-4 py-3 text-sm focus:outline-none focus:border-white transition-colors"
                  />
                </div>

                {/* Textarea */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Textarea
                  </label>
                  <textarea
                    placeholder="Your message"
                    rows={4}
                    className="w-full bg-transparent border border-gray-700 text-white placeholder-gray-600 rounded px-4 py-3 text-sm focus:outline-none focus:border-white transition-colors resize-none"
                  />
                </div>

                {/* Select */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Select
                  </label>
                  <select className="w-full bg-black border border-gray-700 text-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-white transition-colors">
                    <option value="">Choose an option</option>
                    <option>UX Design</option>
                    <option>Product Design</option>
                    <option>Research</option>
                  </select>
                </div>

                {/* Checkbox */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="check"
                    className="w-4 h-4 border border-gray-600 rounded accent-white"
                  />
                  <label htmlFor="check" className="text-sm text-gray-300">
                    I agree to the terms
                  </label>
                </div>
              </div>
            </Section>

            {/* ── Patterns ──────────────────────────────────────────────── */}
            <Section id="patterns">
              <SectionLabel>Patterns</SectionLabel>
              <h2 className="text-2xl font-light text-white mb-10">Recurring UI Patterns</h2>
              <div className="space-y-14">

                {/* Section Label pattern */}
                <div>
                  <p className="text-sm text-gray-500 mb-6">Section Label</p>
                  <div className="border border-gray-800 rounded-lg p-8">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Foundation</p>
                    <h2 className="text-2xl font-light text-white">Section Heading</h2>
                  </div>
                  <p className="text-xs font-mono text-gray-600 mt-3">
                    text-xs font-semibold text-gray-500 uppercase tracking-wider
                  </p>
                </div>

                {/* Meta grid pattern */}
                <div>
                  <p className="text-sm text-gray-500 mb-6">Case Study Meta Grid</p>
                  <div className="border border-gray-800 rounded-lg p-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {[
                        { label: "Role", value: "Lead UX Designer" },
                        { label: "Timeline", value: "Jan to Apr 2025" },
                        { label: "Team", value: "3 designers, 2 devs" },
                        { label: "Platform", value: "Web / Mobile" },
                      ].map((item) => (
                        <div key={item.label}>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            {item.label}
                          </p>
                          <p className="text-sm text-gray-200">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Timeline / Life Notes pattern */}
                <div>
                  <p className="text-sm text-gray-500 mb-6">Timeline Entry</p>
                  <div className="border border-gray-800 rounded-lg p-8">
                    <div className="space-y-8">
                      {[
                        { date: "Mar 2026", text: "Young in the crazy AI world." },
                        { date: "Dec 2025", text: "youngdesign.pro was born." },
                        { date: "Jan 2021", text: "Landed in YVR with 3 luggage bags." },
                      ].map((item) => (
                        <div key={item.date} className="border-l-2 border-gray-700 pl-5">
                          <p className="text-xs text-gray-500 mb-1">{item.date}</p>
                          <p className="text-sm text-gray-200">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider + CTA row */}
                <div>
                  <p className="text-sm text-gray-500 mb-6">Footer CTA Row</p>
                  <div className="border border-gray-800 rounded-lg p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Next Case Study</p>
                        <p className="text-lg font-light text-white">Intranet Connections</p>
                      </div>
                      <button className="flex items-center gap-2 text-sm text-white border border-gray-700 px-5 py-2.5 rounded hover:border-white transition-colors">
                        View Project <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Bottom padding */}
            <div className="py-16">
              <p className="text-xs text-gray-700 font-mono">young.design · design system v1.0 · {new Date().getFullYear()}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DesignSystemGuide;
